import React, { useEffect, useMemo, useState } from 'react';
import {
  meetingService,
  Meeting,
  MeetingMemberStatus,
  MeetingPhoneListEntry,
} from '../../services/meetingService';
import { ProfileService } from '../../services/profileService';
import { formatNameFirstLastInitial } from '../../utils/userState';
import styles from '../../styles/workshop2026Page.module.css';

interface WorkshopPhoneListSectionProps {
  meetingCode: string;
  meeting: Meeting | null;
  memberStatus: MeetingMemberStatus;
  onExpandPreferences?: () => void;
}

function sponsorLabel(willingness: string): string {
  if (willingness === 'yes') return 'Yes';
  if (willingness === 'temporary') return 'Temp';
  return '—';
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const WorkshopPhoneListSection: React.FC<WorkshopPhoneListSectionProps> = ({
  meetingCode,
  meeting,
  memberStatus,
  onExpandPreferences,
}) => {
  const [phoneList, setPhoneList] = useState<MeetingPhoneListEntry[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const phoneListMode = meeting?.phone_list_visibility_mode || 'off';
  const canViewPhoneList =
    phoneListMode !== 'off' &&
    (phoneListMode === 'verified_only'
      ? memberStatus.is_verified ||
        memberStatus.is_manager ||
        memberStatus.is_admin ||
        memberStatus.registration_state === 'confirmed'
      : phoneListMode === 'managers_only'
        ? memberStatus.is_manager || memberStatus.is_admin
        : false);

  const optedIn = memberStatus.include_on_printed_phone_list;

  useEffect(() => {
    if (!canViewPhoneList) {
      setLoading(false);
      setPhoneList([]);
      setCurrentUserId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [res, profile] = await Promise.all([
          meetingService.getPhoneList(meetingCode),
          ProfileService.getUserProfile().catch(() => null),
        ]);
        if (cancelled) return;
        const list = Array.isArray(res.phone_list) ? res.phone_list : [];
        const uid = profile?.user_id || null;
        setCurrentUserId(uid);

        if (optedIn && uid && profile && !list.some((e) => e.user_id === uid)) {
          list.push({
            user_id: uid,
            display_name: profile.display_name || formatNameFirstLastInitial(profile.display_name, 'Member'),
            phone: (profile.phone || '').trim(),
            email: memberStatus.include_email_on_list ? profile.user_id : undefined,
            sponsor_willingness: memberStatus.sponsor_willingness,
          });
        }

        list.sort((a, b) =>
          (a.display_name || '').localeCompare(b.display_name || '', undefined, { sensitivity: 'base' })
        );
        setPhoneList(list);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load phone list');
          setPhoneList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingCode, canViewPhoneList, optedIn, memberStatus.include_email_on_list, memberStatus.sponsor_willingness]);

  const displayList = useMemo(
    () =>
      phoneList.map((entry) => ({
        ...entry,
        display_name: formatNameFirstLastInitial(entry.display_name, entry.display_name || 'Member'),
      })),
    [phoneList]
  );

  const handlePrint = () => {
    if (displayList.length === 0) return;
    const printWindow = window.open('', '_blank', 'width=480,height=720');
    if (!printWindow) return;
    const workshopName = meeting?.name || '2026 SAA Step Workshop';
    const asOf = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const rows = displayList
      .map(
        (entry) => `
      <tr>
        <td>${escapeHtml(entry.display_name)}</td>
        <td class="col-phone">${escapeHtml(entry.phone || '—')}</td>
        <td class="col-email">${escapeHtml(entry.email || '—')}</td>
        <td>${escapeHtml(sponsorLabel(entry.sponsor_willingness))}</td>
      </tr>`
      )
      .join('');

    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${escapeHtml(workshopName)} — Phone list</title>
<style>
  @page { margin: 0.85in 0.35in 0.35in 0.35in; size: auto; }
  body { font-family: "Courier New", Courier, monospace; font-size: 9pt; line-height: 1.2; color: #000; margin: 0; padding: 0.15in 0 0; }
  h1 { font-size: 10pt; font-weight: bold; margin: 0.35in 0 2px; }
  .meta { font-size: 8pt; margin: 0 0 4px; }
  .privacy { font-size: 7.5pt; margin: 0 0 6px; line-height: 1.25; }
  table { width: auto; max-width: 100%; border-collapse: collapse; table-layout: fixed; }
  col.col-name { width: 22%; }
  col.col-phone { width: 15ch; }
  col.col-email { width: 38%; }
  col.col-sponsor { width: 4.5em; }
  th, td { text-align: left; padding: 1px 6px 1px 0; vertical-align: top; border-bottom: 1px solid #ccc; }
  th { font-size: 8pt; font-weight: bold; border-bottom: 1px solid #000; }
  td.col-phone { white-space: nowrap; }
  td.col-email { word-wrap: break-word; overflow-wrap: anywhere; }
</style></head><body>
<h1>${escapeHtml(workshopName)}</h1>
<p class="meta">Phone list · ${escapeHtml(asOf)}</p>
<p class="privacy">Private — for workshop members only. Please keep this information confidential and do not share outside the workshop.</p>
<table>
  <colgroup>
    <col class="col-name" />
    <col class="col-phone" />
    <col class="col-email" />
    <col class="col-sponsor" />
  </colgroup>
  <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Sponsor</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const openPreferences = () => {
    onExpandPreferences?.();
    document.getElementById('workshop-preferences')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.phoneListInner}>
      <p className={styles.helperText}>
        Members who opted in to share their contact information with confirmed workshop members.
      </p>

      {phoneListMode === 'off' && (
        <p className={styles.helperText}>
          The phone list is not available yet. The facilitator can enable it in workshop settings.
        </p>
      )}

      {phoneListMode !== 'off' && !canViewPhoneList && (
        <p className={styles.helperText}>
          You need to be a verified confirmed member to view the phone list.
        </p>
      )}

      {canViewPhoneList && loading && <p className={styles.helperText}>Loading phone list…</p>}
      {canViewPhoneList && !loading && error && <p className={styles.helperText}>{error}</p>}
      {canViewPhoneList && !loading && !error && displayList.length === 0 && (
        <p className={styles.helperText}>
          No members have opted in yet. Update your preferences below to add your number.
        </p>
      )}

      {canViewPhoneList && !loading && !error && displayList.length > 0 && (
        <>
          <div className={styles.phoneListTableWrap}>
            <table className={styles.phoneListTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Sponsor?</th>
                </tr>
              </thead>
              <tbody>
                {displayList.map((entry) => {
                  const isYou = !!(currentUserId && entry.user_id === currentUserId);
                  return (
                    <tr
                      key={entry.user_id}
                      className={isYou ? styles.phoneListRowYou : undefined}
                    >
                      <td>
                        {entry.display_name}
                        {isYou ? ' (you)' : ''}
                      </td>
                      <td>{entry.phone || '—'}</td>
                      <td>{entry.email || '—'}</td>
                      <td>{sponsorLabel(entry.sponsor_willingness)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={styles.phoneListActions}>
            <button type="button" className={styles.secondaryBtn} onClick={handlePrint}>
              Print phone list
            </button>
            <button type="button" className={styles.textButton} onClick={openPreferences}>
              Update my preferences
            </button>
          </div>
        </>
      )}

      {canViewPhoneList && !loading && !error && displayList.length === 0 && (
        <button type="button" className={styles.textButton} onClick={openPreferences}>
          Update my preferences
        </button>
      )}
    </div>
  );
};

export default WorkshopPhoneListSection;
